import { cellText, fold } from "@ui/format";

/** What a column sorts and filters by. */
export type CellValue = string | number | boolean | Date | null | undefined;

/** The column order a person sees: their saved order of the columns still there, a column they never placed (new, or
 *  added by the page later) right after the column it follows by default. ``fixed`` columns (a tick box, the row's
 *  buttons) are not moved: they keep their own places. */
export function orderColumns(
  defaults: readonly string[],
  saved: readonly string[] | undefined,
  fixed: ReadonlySet<string> = new Set(),
): string[] {
  const movable = defaults.filter((id) => !fixed.has(id));
  const known = new Set(movable);
  const out = (saved ?? []).filter((id, i, all) => known.has(id) && all.indexOf(id) === i);
  const placed = new Set(out);
  movable.forEach((id, i) => {
    if (placed.has(id)) return;
    out.splice(i === 0 ? 0 : out.indexOf(movable[i - 1] as string) + 1, 0, id);
    placed.add(id);
  });
  defaults.forEach((id, i) => {
    if (fixed.has(id)) out.splice(i, 0, id);
  });
  return out;
}

/** ``order`` (the movable columns) with ``id`` moved to position ``to`` (0 = first). */
export function moveColumn(order: readonly string[], id: string, to: number): string[] {
  if (!order.includes(id)) return [...order];
  const out = order.filter((c) => c !== id);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, id);
  return out;
}

const asNumber = (v: CellValue): number | null =>
  typeof v === "number" ? v : v instanceof Date ? v.getTime() : typeof v === "boolean" ? Number(v) : null;

const NUMBER = String.raw`(-?\d[\d\s]*(?:[.,]\d+)?)`;
const RANGE = new RegExp(String.raw`^${NUMBER}\s*\.\.\s*${NUMBER}$`);
const COMPARE = new RegExp(String.raw`^(<=|>=|<|>|=)\s*${NUMBER}$`);
// a typed limit: a decimal comma or point, spaces between thousands
const number = (text: string) => {
  const n = Number(text.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const COMPARISONS: Record<string, (n: number, limit: number) => boolean> = {
  "<": (n, l) => n < l,
  "<=": (n, l) => n <= l,
  ">": (n, l) => n > l,
  ">=": (n, l) => n >= l,
  "=": (n, l) => n === l,
};

/** A column's filter text as a test of a cell (``value``, and ``shown``: the cell as text), or null when it lets every
 *  row through. Text matches anywhere, upper or lower case, accents or none ("kor" finds "Kör"); ``=kor`` only the
 *  whole cell (the legacy "Match"), ``=`` alone the empty cells. A number column also takes ``>5``, ``<=10``, ``=3``
 *  and ``2..8`` (both ends included). */
export function compileFilter(filter: string, numeric: boolean): ((value: CellValue, shown: string) => boolean) | null {
  const f = filter.trim();
  if (!f) return null;
  if (f === "=") return (value, shown) => blank(value) || !shown.trim();
  if (numeric) {
    const range = RANGE.exec(f);
    const [low, high] = range ? [number(range[1] as string), number(range[2] as string)] : [null, null];
    if (low !== null && high !== null) {
      const [from, to] = [Math.min(low, high), Math.max(low, high)];
      return (value) => {
        const n = asNumber(value);
        return n !== null && n >= from && n <= to;
      };
    }
    const compare = COMPARE.exec(f);
    const limit = compare ? number(compare[2] as string) : null;
    const test = compare && COMPARISONS[compare[1] as string];
    if (test && limit !== null)
      return (value) => {
        const n = asNumber(value);
        return n !== null && test(n, limit);
      };
  }
  if (f.startsWith("=")) {
    const whole = fold(f.slice(1).trim());
    return (_value, shown) => fold(shown.trim()) === whole;
  }
  const needle = fold(f);
  return (_value, shown) => fold(shown).includes(needle);
}

/** An empty cell: nothing, empty text or not a number. */
export const blank = (v: CellValue) => v === null || v === undefined || v === "" || (typeof v === "number" && Number.isNaN(v));

/** Two cells in ascending order: numbers and dates by size, text the way the language sorts it with numbers inside
 *  text by their value ("A2" before "A10"). Blank cells go last either way. */
export function compareCells(a: CellValue, b: CellValue, collator: Intl.Collator, descending = false): number {
  if (blank(a) || blank(b)) return blank(a) === blank(b) ? 0 : blank(a) ? 1 : -1;
  const na = asNumber(a);
  const nb = asNumber(b);
  const order = na !== null && nb !== null ? na - nb : collator.compare(String(a), String(b));
  return descending ? -order : order;
}

export type ViewKey<T> = { value: (row: T) => CellValue; descending: boolean };
export type ViewFilter<T> = { value: (row: T) => CellValue; shown: (row: T) => string; test: (value: CellValue, shown: string) => boolean };

/** The rows a table shows: those every filter lets through, in the order of the sort keys (stable: rows that tie keep
 *  their own order). */
export function viewRows<T>(
  rows: readonly T[],
  keys: readonly ViewKey<T>[],
  filters: readonly ViewFilter<T>[],
  collator: Intl.Collator,
): T[] {
  const kept = filters.length ? rows.filter((row) => filters.every((f) => f.test(f.value(row), f.shown(row)))) : [...rows];
  if (!keys.length) return kept;
  // each cell read once, not once per comparison
  const decorated = kept.map((row, i) => ({ row, i, cells: keys.map((k) => k.value(row)) }));
  decorated.sort((a, b) => {
    for (let k = 0; k < keys.length; k++) {
      const d = compareCells(a.cells[k], b.cells[k], collator, keys[k]?.descending);
      if (d) return d;
    }
    return a.i - b.i;
  });
  return decorated.map((d) => d.row);
}

/** A cell as text for the filter, when the column gives no text of its own (a number with a decimal point and with a
 *  decimal comma, so "4,5" and "4.5" both find it; yes / no as the cell shows it). */
export function filterText(v: CellValue): string {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "boolean") return cellText(v);
  if (typeof v === "number" && !Number.isInteger(v)) return `${v} ${String(v).replace(".", ",")}`;
  return String(v);
}
